// 统一走 /api 前缀:
// - dev: Vite proxy 把 /api/* 透传到本地后端 :8080(后端 router 直接挂 /api 下,见 backend/src/index.ts)
// - prod (SWA + Linked Backend): SWA 把 /api/* 转发到 App Service,backend 同样吃 /api/*
// - 如果要直连其它后端(子域名/跨站),设 VITE_API_BASE=https://api.example.com 走绝对地址
const API_BASE = import.meta.env.VITE_API_BASE || "/api";

// Build an absolute API path (e.g. for <img src> / downloads) that respects the
// same base + /api prefix the fetch client uses. Without this, raw "/messages/…"
// hits the SPA fallback in dev and isn't routed to the backend in prod.
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

type Options = Omit<RequestInit, "body"> & { body?: unknown };

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  const isMutation = !["GET", "HEAD", "OPTIONS"].includes((opts.method ?? "GET").toUpperCase());
  if (isMutation) {
    const csrf = readCookie("pm_csrf");
    if (csrf) headers.set("X-CSRF", csrf);
    if (!headers.has("Content-Type") && opts.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // ignore
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  const ctype = res.headers.get("content-type") ?? "";
  if (ctype.includes("application/json")) return (await res.json()) as T;
  if (ctype.includes("text/html")) {
    throw new ApiError(`unexpected_html_response (${path})`, res.status);
  }
  return (await res.text()) as unknown as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
